import React from "react";
import {Button, Checkbox, Grid, List, ListItem, ListItemIcon, ListItemText, Paper} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import {ChevronLeft, ChevronRight, FastRewind} from "@mui/icons-material";

export interface ITransferListProps<T> {
	left: T[];
	right: T[];
	getKey: (item: T) => string;
	renderItem: (item: T) => React.ReactNode|string;
	leftHeader: string;
	rightHeader: string;
	onChange: (left: T[], right: T[]) => void;
	showCopy?: boolean;
	showMoveAll?: boolean;
}

export function TransferList<T>(props:  ITransferListProps<T>) {
	const [checked, setChecked] = React.useState<readonly T[]>([]);
	
	const leftChecked = intersection(checked, props.left);
	const rightChecked = intersection(checked, props.right);
	
	function not(a: readonly T[], b: readonly T[]) {
		return a.filter((value) => b.indexOf(value) === -1);
	}
	
	function intersection(a: readonly T[], b: readonly T[]) {
		return a.filter((value) => b.indexOf(value) !== -1);
	}
	const handleToggle = (value: T) => () => {
		const currentIndex = checked.indexOf(value);
		const newChecked = [...checked];
		
		if (currentIndex === -1) {
			newChecked.push(value);
		} else {
			newChecked.splice(currentIndex, 1);
		}
		
		setChecked(newChecked);
	};
	
	const handleAllRight = () => {
		props.onChange([], props.right.concat(props.left));
	};
	
	const handleCheckedRight = () => {
		props.onChange(not(props.left, leftChecked),props.right.concat(leftChecked) );
		setChecked(not(checked, leftChecked));
	};
	
	const handleCheckedCopyRight = () => {
		props.onChange(props.left,props.right.concat(leftChecked) );
		setChecked(not(checked, leftChecked));
	};
	
	const handleCheckedCopyLeft = () => {
		props.onChange(props.left.concat(rightChecked), props.right);
		setChecked(not(checked, leftChecked));
	};
	
	const handleCheckedLeft = () => {
		props.onChange(props.left.concat(rightChecked), not(props.right, rightChecked));
		setChecked(not(checked, rightChecked));
	};
	
	const handleAllLeft = () => {
		props.onChange(props.left.concat(props.right), []);
	};
	
	const customList = (items: readonly T[], header: string) => (
		<Paper sx={{ width: 350, height: 530, overflow: "auto" }}>
			{header}
			<List dense component="div" role="list">
				{items.map((value: T) => {
					const labelId = `transfer-list-item-${value}-label`;
					
					return (
						<ListItem
							key={props.getKey(value)}
							role="listitem"
							onClick={handleToggle(value)}
						>
							<ListItemIcon>
								<Checkbox
									checked={checked.indexOf(value) !== -1}
									tabIndex={-1}
									disableRipple
									inputProps={{
										"aria-labelledby": labelId,
									}}
								/>
							</ListItemIcon>
							<ListItemText id={labelId} primary={props.renderItem(value)} />
						</ListItem>
					);
				})}
			</List>
		</Paper>
	);
	
	return (
		<Grid container spacing={2} justifyContent="center" alignItems="center">
			<Grid item>{customList(props.left, props.leftHeader)}</Grid>
			<Grid item>
				<Grid container direction="column" alignItems="center">
					{props.showMoveAll &&
					<Button
						sx={{ my: 0.5 }}
						variant="outlined"
						size="small"
						onClick={handleAllRight}
						disabled={props.left.length === 0}
						aria-label="move all right"
					>
						≫
					</Button>
					}
					<Button
						sx={{ my: 0.5 }}
						variant="outlined"
						size="small"
						onClick={handleCheckedRight}
						disabled={leftChecked.length === 0}
						aria-label="move selected right"
					>
						<ChevronRight/>
					</Button>

					<Button
						sx={{ my: 0.5 }}
						variant="outlined"
						size="small"
						onClick={handleCheckedCopyRight}
						disabled={leftChecked.length === 0}
						aria-label="add selected right"
					>
						<AddIcon/>
						<ChevronRight/>
					</Button>
					<Button
						sx={{ my: 0.5 }}
						variant="outlined"
						size="small"
						onClick={handleCheckedCopyLeft}
						disabled={rightChecked.length === 0}
						aria-label="add selected left"
					>
						<AddIcon/>
						<ChevronLeft/>
					</Button>
					
					<Button
						sx={{ my: 0.5 }}
						variant="outlined"
						size="small"
						onClick={handleCheckedLeft}
						disabled={rightChecked.length === 0}
						aria-label="move selected left"
					>
						<ChevronLeft/>
					</Button>
					{props.showMoveAll &&
					<Button
						sx={{ my: 0.5 }}
						variant="outlined"
						size="small"
						onClick={handleAllLeft}
						disabled={props.right.length === 0}
						aria-label="move all left"
					>
						<FastRewind/>
					</Button>
					}
				</Grid>
			</Grid>
			<Grid item>{customList(props.right, props.rightHeader)}</Grid>
		</Grid>
	);

}
